<template>
    <div class="insured-info">
        <div class="insured-info-grid">
            <div class="insured-info-panel soft-card">
                <div class="insured-info-panel__head">
                    <h5>{{ $t("commonGeneralInfo") }}</h5>
                </div>
                <dl class="insured-info-list">
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonFullName") }}</dt>
                        <dd class="insured-info-list__value--primary">{{ displayValue(info.insuredName) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>PIN</dt>
                        <dd>{{ displayValue(info.pin) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonSex") }}</dt>
                        <dd>{{ displaySex(info) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonBirthDate") }}</dt>
                        <dd>{{ displayBirthDate(info.dob) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonPhone") }}</dt>
                        <dd>{{ displayPhone(info.phone) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonAddress") }}</dt>
                        <dd>{{ displayValue(info.address) }}</dd>
                    </div>
                </dl>
            </div>

            <div class="insured-info-panel soft-card">
                <div class="insured-info-panel__head">
                    <h5>{{ $t("commonInsurance") }}</h5>
                </div>
                <dl class="insured-info-list">
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonProgram") }}</dt>
                        <dd class="insured-info-list__value--primary">{{ displayValue(info.groupName) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonStatus") }}</dt>
                        <dd>
                            {{ displayValue(info.employeeStatusName) }}
                            <span v-if="info.vip" class="insured-info-vip"> · VIP</span>
                        </dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("insuredAcceptDate") }}</dt>
                        <dd>{{ displayValue(info.startDateStr) }}</dd>
                    </div>
                    <div class="insured-info-list__row">
                        <dt>{{ $t("commonEndDate") }}</dt>
                        <dd>{{ displayValue(info.endDateStr) }}</dd>
                    </div>
                    <div class="insured-info-list__row" :class="{ 'insured-info-list__row--cancel': info.cancelDate }">
                        <dt>{{ $t("insuredCancelDate") }}</dt>
                        <dd>{{ info.cancelDate ? info.cancelDateStr : "—" }}</dd>
                    </div>
                    <div class="insured-info-list__row insured-info-list__row--ecard">
                        <dt>{{ $t("commonECard") }}</dt>
                        <dd>
                            <a
                                id="textToCopy"
                                class="insured-info-ecard-link"
                                :href="'https://ecard.ateshgah.com/' + info.linkId"
                                target="_blank"
                                rel="noopener noreferrer"
                                :title="'https://ecard.ateshgah.com/' + info.linkId"
                            >ecard.ateshgah.com/{{ info.linkId || "—" }}</a>
                            <el-button
                                type="primary"
                                class="insured-info-copy"
                                size="small"
                                :icon="CopyDocument"
                                circle
                                @click="copy"
                            />
                        </dd>
                    </div>
                </dl>
            </div>

            <div class="insured-info-panel soft-card insured-info-panel--family">
                <div class="insured-info-panel__head d-flex justify-content-between align-items-center flex-wrap gap-1">
                    <h5>{{ $t("insuredFamily") }}</h5>
                    <span v-if="!info.isEmployee && mainEmployeePerson" class="text-xs text-muted">
                        {{ $t("commonEmployee") }}: 
                        <a class="table-link fw-bold text-primary" role="button" @click="goTo(mainEmployeePerson)">
                            <i class="bi bi-person-badge-fill me-1"></i>{{ mainEmployeePerson.dependantInsuredName }}
                        </a>
                    </span>
                </div>
                <div class="insured-info-family">
                    <button type="button" class="action-btn action-btn--info action-btn--compact insured-info-family__add" @click="dialogVis = true">
                        <i class="bi bi-person-plus" aria-hidden="true"></i>
                        <span>{{ $t("commonAdd") }}</span>
                    </button>
                    <div v-loading="familyLoading" class="insured-info-family__table">
                        <el-table
                            border
                            :row-class-name="tableRowClassName"
                            size="default"
                            class="el-table-css insured-family-table"
                            :data="dataFamily"
                            empty-text="—"
                        >
                            <el-table-column :label="$t('commonName')">
                                <template #default="scope">
                                    <div class="d-flex align-items-center justify-content-between gap-2 min-w-0">
                                        <a class="table-link d-flex align-items-center gap-1 min-w-0 text-truncate" :class="{ 'fw-bold': isMainEmployee(scope.row) }" role="button" @click="goTo(scope.row)">
                                            <i :class="isMainEmployee(scope.row) ? 'bi bi-person-badge-fill text-primary' : 'bi bi-person text-secondary'"></i>
                                            <span class="text-truncate">{{ scope.row.dependantInsuredName }}</span>
                                        </a>
                                        <span v-if="isMainEmployee(scope.row)" class="badge badge--info text-xs text-nowrap">
                                            <i class="bi bi-star-fill me-1 text-warning"></i>{{ $t("commonEmployee") }}
                                        </span>
                                        <span v-else class="badge badge--muted text-xs text-nowrap">
                                            {{ $t("commonFamilyMember") }}
                                        </span>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column width="56" align="right">
                                <template #default="scope">
                                    <el-button type="danger" :icon="Delete" text @click="deleteRelate(scope.row)" />
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </div>
        </div>

        <div class="insured-info-requests">
            <div class="card soft-card soft-table-card">
                <div class="soft-card-header">
                    <h6>{{ $t("requestTitle") }}</h6>
                </div>
                <div class="card-body insured-info-requests__body">
                    <request-table
                            :data-table="dataTable"
                            @get-info="infoRequest"
                            @get-req-table="getReqTable"
                        />
                </div>
            </div>
        </div>

        <el-dialog
            v-model="dialogVis"
            :close-on-click-modal="false"
            :title="$t('insuredFamilyMember')"
            modal-class="modal-class"
            width="560px"
            :destroy-on-close="true"
            align-center
        >
            <RelateDialog
                :policy-id="info.policyId"
                :is-employee="info.isEmployee"
                :insured-name="info.insuredName"
                @close="dialogVis = false"
                @insert="dialogVis = false; getFamily()"
            />
        </el-dialog>
    </div>
</template>

<script>
import { getCardId } from "@/project/Source/routes";

import { Delete } from '@element-plus/icons-vue'
import { CopyDocument } from '@element-plus/icons-vue'
import RequestTable from './RequestTable.vue';
import Source from "../../Source/claim.js"
import { weblink, formatAzMobileFull, swalDialogClass } from '@/project/Source/helper.js'
import RelateDialog from './RelateDialog.vue';
export default {
    components: { RequestTable, RelateDialog },
    props: {
        info: {
            type: Object,
            default: () => ({})
        },
        dataTable: {
            type: Array,
            default: () => []
        },
        claimDataTable: {
            type: Array,
            default: () => []
        }
    },
    emits: ["get-info", "get-req-table", "open-op-modal"],
    data() {
        return {
            loadingTable: false,
            dataFamily: [],
            CopyDocument,
            Delete,
            dialogVis: false,
            familyLoading: false,
        };
    },
    computed: {
        claimHeight() {
            if (this.dataTable != null) {
                var length = this.dataTable.length

                if (length > 5) length = 5
                if (length == 0) length = 1

                return (633 - 55 * (5 - length)).toString()
            }

            return null
        },
        mainEmployeePerson() {
            if (!this.dataFamily || !this.dataFamily.length) return null;
            return this.dataFamily.find((item) => this.isMainEmployee(item)) || null;
        }
    },
    watch: {
        info() {
            this.getFamily()
        }
    },
    methods: {
        isMainEmployee(row) {
            if (!row) return false;
            if (row.dependantInsuredId && row.mainInsuredId && Number(row.dependantInsuredId) === Number(row.mainInsuredId)) {
                return true;
            }
            return false;
        },
        displayValue(value) {
            if (value === null || value === undefined || value === "") {
                return "—";
            }
            return value;
        },
        displaySex(info) {
            if (!info) {
                return "—";
            }
            if (info.sex === "m" || info.sex === "M") {
                return this.$t("commonMale");
            }
            if (info.sex === "f" || info.sex === "F") {
                return this.$t("commonFemale");
            }
            return this.displayValue(info.sexStr);
        },
        displayBirthDate(value) {
            if (!value) {
                return "—";
            }
            const raw = String(value).slice(0, 10);
            const parts = raw.split("-");
            if (parts.length === 3) {
                return `${parts[2]}.${parts[1]}.${parts[0]}`;
            }
            return raw;
        },
        displayPhone(value) {
            const formatted = formatAzMobileFull(value);
            return formatted || "—";
        },
        tableRowClassName(row) {
            if (row.row.dependantInsuredId == row.row.mainInsuredId) {
                return "selected-row";
            }
        },
        getReqTable(val) {
            if (val == 1) {
                this.loadingTable = true
                setTimeout(() => { this.loadingTable = false }, 1000)
            }
            this.$emit('get-req-table')
        },
        infoRequest(arId) {
            this.$emit('get-info', arId)
        },
        goTo(row) {
            var url = weblink + "claim/insured/" + row.dependantCardId + "/" + JSON.parse(sessionStorage.getItem('policy'));
            window.open(url, "_blank");
        },
        goToMain() {
            var url = weblink + "claim/insured/" + this.info.cardId2 + "/" + JSON.parse(sessionStorage.getItem('policy'));
            window.open(url, "_blank");
        },
        copy() {
            const textElement = document.getElementById('textToCopy');
            const text = textElement.href;
            navigator.clipboard.writeText(text)
        },
        getFamily() {
            this.familyLoading = true
            const data = {
                id: getCardId(this.$route),
                requestCorrelationId: "string"
            }
            if (this.info.isEmployee == true) {
                Source.PolicyInsuredDependants(data).then((responseFamily) => {
                    this.dataFamily = responseFamily.data.data
                    this.familyLoading = false
                })
            } else {
                Source.PolicyAllInsuredDependants(data).then((responseFamily) => {
                    this.dataFamily = responseFamily.data.data
                    this.familyLoading = false
                })
            }
        },
        openFamilyOpModal(opType, row) {
            const cardData = {
                cardId: row.dependantCardId || row.cardId || row.dependantInsuredId,
                insuredName: row.dependantInsuredName || row.insuredName,
                pin: row.pin,
                policyId: this.info?.policyId || 0,
                groupId: row.groupId || this.info?.groupId || 0,
                groupName: row.groupName || this.info?.groupName || "",
                isEmployee: false,
                ...row
            };
            this.$emit('open-op-modal', opType, cardData);
        },
        async deleteRelate(row) {
            this.$swal({
                title: "Əminsiniz?",
                text: this.$t("confirmRemoveFamily"),
                icon: "warning",
                showCancelButton: true,
                cancelButtonText: this.$t("commonCancel"),
                confirmButtonText: this.$t("commonConfirm"),
                customClass: swalDialogClass(),
                buttonsStyling: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    const deleteData = {
                        "requestCorrelationId": Date.now().toString(36) + Math.random().toString(36).substr(2),
                        "data": {
                            "dependantId": row.dependantId
                        }
                    }
                    Source.DropPolicyInsuredDependantRequest(deleteData)
                        .then((response) => {
                            if (response.data.isSuccess == undefined) {
                                this.alertColor = 'danger'
                                this.errorId = deleteData.requestCorrelationId
                            } else {
                                this.getFamily()
                                this.alertColor = 'success'
                            }
                            this.Notif();
                        })

                } else if (result.dismiss === this.$swal.DismissReason.cancel) {
                    this.$swal.dismiss;
                }
            });
        },
    }
};
</script>

<style scoped>
.insured-info {
  width: 100%;
}

.insured-info-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.15fr) minmax(220px, 0.8fr);
  gap: 1rem;
  align-items: stretch;
  margin-bottom: 1rem;
}

.insured-info-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  margin: 0;
  overflow: hidden;
  transition: none;
}

.insured-info-panel:hover {
  transform: none;
  box-shadow: var(--app-shadow);
}

.insured-info-panel__head {
  flex: 0 0 auto;
  padding: 0.85rem 1.15rem;
  border-bottom: 1px solid var(--app-border);
}

.insured-info-panel__head h5 {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 650;
  color: var(--app-heading);
  line-height: 1.25;
}

.insured-info-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin: 0;
  padding: 0;
}

.insured-info-list__row {
  display: grid;
  grid-template-columns: minmax(7.5rem, 38%) minmax(0, 1fr);
  gap: 0.75rem 1rem;
  align-items: center;
  min-height: 2.85rem;
  padding: 0.55rem 1.15rem;
  border-bottom: 1px solid var(--app-border);
}

.insured-info-list__row:last-child {
  border-bottom: 0;
}

.insured-info-list__row dt {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-text);
  line-height: 1.45;
}

.insured-info-list__row dd {
  margin: 0;
  min-width: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--app-heading);
  line-height: 1.45;
  word-break: break-word;
}

.insured-info-list__value--primary {
  font-weight: 700;
}

.insured-info-vip {
  color: var(--bs-warning);
  font-weight: 700;
}

.insured-info-list__row--cancel dd {
  color: #dc2626;
}

.insured-info-list__row--ecard dd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.insured-info-ecard-link {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--bs-info);
  text-decoration: none;
}

.insured-info-ecard-link:hover {
  text-decoration: underline;
}

.insured-info-copy {
  flex: 0 0 auto;
  width: 1.75rem !important;
  height: 1.75rem !important;
}

.insured-info-family {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 0.75rem;
  min-height: 0;
  padding: 0.95rem 1.1rem 1.05rem;
}

.insured-info-family__add {
  width: 100%;
  margin: 0;
}

.insured-info-family__table {
  flex: 1 1 auto;
  min-height: 0;
}

.insured-family-table {
  --el-table-row-hover-bg-color: transparent;
}

.insured-family-table :deep(.el-table__empty-block) {
  min-height: 4.5rem;
  background: var(--app-surface-muted);
  border: 1px dashed var(--app-border);
  border-radius: var(--app-radius-sm);
}

.insured-family-table :deep(.el-table__empty-text) {
  color: var(--app-text);
  opacity: 0.55;
}

.insured-family-table :deep(.el-table__header-wrapper th.el-table__cell) {
  background: transparent;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--app-text);
}

.insured-info-requests {
  width: 100%;
}

.insured-info-requests__body {
  padding: 0 !important;
}

@media (max-width: 1199.98px) {
  .insured-info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .insured-info-panel--family {
    grid-column: 1 / -1;
  }
}

@media (max-width: 767.98px) {
  .insured-info-grid {
    grid-template-columns: 1fr;
  }

  .insured-info-list__row {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
}

.insured-row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.insured-action-sq-btn {
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 0.75rem;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.insured-action-sq-btn--edit {
  background: color-mix(in srgb, #3b82f6 12%, transparent);
  border-color: color-mix(in srgb, #3b82f6 30%, transparent);
  color: #2563eb;
}

.insured-action-sq-btn--edit:hover {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
}

.insured-action-sq-btn--program {
  background: color-mix(in srgb, #f59e0b 12%, transparent);
  border-color: color-mix(in srgb, #f59e0b 30%, transparent);
  color: #d97706;
}

.insured-action-sq-btn--program:hover {
  background: #f59e0b;
  border-color: #f59e0b;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(245, 158, 11, 0.3);
}

.insured-action-sq-btn--terminate {
  background: color-mix(in srgb, #ef4444 12%, transparent);
  border-color: color-mix(in srgb, #ef4444 30%, transparent);
  color: #dc2626;
}

.insured-action-sq-btn--terminate:hover {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.3);
}
</style>
